// Heatmap "Rotación de mesas" — componente React/Tremor.
// Filas = zonas del salón; columnas = meses. Cada celda muestra la rotación
// del mes (veces que cada mesa se ocupa). Reutiliza colorFor() de heatmapLogic
// para mantener la misma paleta azul corporativa que "Ventas por Franja Horaria".
import { Card, Title, Text } from "@tremor/react";
import { colorFor } from "../lib/heatmapLogic";

export interface ZonaRotacion {
  zona: string;
  asientos: number;
}
export interface MesRotacion {
  mm: number;
  label: string;
  comensales: number | null;
  mesas_disponibles: number;
  rotacion: number | null;
}
export interface RotacionMesasData {
  anio: number;
  zonas: ZonaRotacion[];
  meses: MesRotacion[];
}

export default function HeatmapRotacionMesas({ data }: { data: RotacionMesasData }) {
  const zonas = data.zonas;
  const meses = data.meses;

  // Rango de rotación para la escala de color (ignora nulls)
  const vals = meses.map((m) => m.rotacion).filter((v): v is number => v != null);
  const min = vals.length ? Math.min(...vals) : 0;
  const max = vals.length ? Math.max(...vals) : 1;

  return (
    <Card className="!p-3">
      <Title className="!text-base">Rotación de mesas</Title>
      <Text className="!text-xs">
        N° comensales atendidos ÷ N° de mesas disponibles · {data.anio}
      </Text>

      <div className="mt-3 flex gap-4">
        {/* Matriz */}
        <div className="flex-1 overflow-x-auto">
          <table className="w-full border-collapse" style={{ borderSpacing: 0 }}>
            <thead>
              <tr>
                <th className="text-left text-[11px] font-medium text-tremor-content p-1">
                  Zona
                </th>
                {meses.map((m) => (
                  <th
                    key={m.mm}
                    className="text-center text-[11px] font-medium text-tremor-content p-1 whitespace-nowrap"
                  >
                    {m.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {zonas.length === 0 && (
                <tr>
                  <td colSpan={meses.length + 1} className="text-center text-[11px] text-tremor-content p-3">
                    Sin inventario de mesas cargado
                  </td>
                </tr>
              )}
              {zonas.map((z) => (
                <tr key={z.zona}>
                  <td className="text-left text-[11px] font-medium text-tremor-content-emphasis p-1 whitespace-nowrap">
                    {z.zona}
                  </td>
                  {meses.map((m) => {
                    const { bg, fg } = colorFor(m.rotacion, min, max);
                    return (
                      <td key={m.mm} className="p-0.5">
                        <div
                          className="flex items-center justify-center rounded-sm"
                          style={{
                            background: bg,
                            color: fg,
                            height: 34,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                          title={`${z.zona} · ${m.label}: ${
                            m.rotacion != null ? `${m.rotacion} veces` : "s/d"
                          } (${m.comensales ?? 0} comensales ÷ ${m.mesas_disponibles} mesas)`}
                        >
                          {m.rotacion == null ? "s/d" : m.rotacion}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Leyenda vertical */}
        <div
          className="flex flex-col items-center justify-center shrink-0"
          style={{ width: 54 }}
        >
          <span className="text-[10px] text-tremor-content mb-1">Mayor</span>
          <div
            className="rounded-sm"
            style={{
              width: 16,
              height: 120,
              background: "linear-gradient(to bottom, #15335B, #6F91B3)",
            }}
          />
          <span className="text-[10px] text-tremor-content mt-1">Menor</span>
        </div>
      </div>

      <Text className="!text-[10px] mt-2 text-tremor-content">
        La rotación se calcula a nivel de restaurante por mes (los comensales de
        Ocupabilidad no se desagregan por zona), por eso cada zona muestra el mismo
        valor del mes.
      </Text>
    </Card>
  );
}
